const DENIED_SCHEMES = new Set([
  'javascript',
  'vbscript',
  'data',
  'blob',
  'file',
  'about',
  'chrome',
  'chrome-extension',
  'moz-extension',
  'safari-extension',
]);

const SCHEME_CHARACTERS = /^[a-z][a-z0-9+.-]*$/i;

const isLeadingCharacterBrowsersIgnore = (character: string): boolean => {
  const code = character.charCodeAt(0);
  return code <= 0x1f || code === 0x20;
};

const stripCharactersBrowsersIgnore = (value: string): string => {
  const withoutLineBreaks = value.replace(/[\t\n\r]/g, '');
  let index = 0;
  while (
    index < withoutLineBreaks.length &&
    isLeadingCharacterBrowsersIgnore(withoutLineBreaks[index])
  ) {
    index += 1;
  }
  return withoutLineBreaks.slice(index);
};

const decodeScheme = (value: string): string | null => {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
};

// Percent-decode the scheme (and the whole value when the colon itself is
// encoded) so `javascript%3A` and `java%73cript:` are judged as javascript.
const urlWithDecodedScheme = (value: string): string | null => {
  const colonAt = value.indexOf(':');
  if (colonAt === -1) return decodeScheme(value);

  const scheme = decodeScheme(value.slice(0, colonAt));
  if (!scheme || !SCHEME_CHARACTERS.test(scheme)) return null;
  return `${scheme}${value.slice(colonAt)}`;
};

const parseUrl = (value: string): URL | null => {
  try {
    return new URL(value);
  } catch {
    return null;
  }
};

const schemeOf = (url: URL): string => url.protocol.slice(0, -1).toLowerCase();

const isAllowedRedirect = (url: URL): boolean => !DENIED_SCHEMES.has(schemeOf(url));

export const sanitizeMerchantRedirectUrl = (raw: string | null | undefined): string | null => {
  if (typeof raw !== 'string') return null;

  const stripped = stripCharactersBrowsersIgnore(raw).trim();
  if (!stripped) return null;

  const decoded = urlWithDecodedScheme(stripped);
  if (!decoded) return null;

  const decodedUrl = parseUrl(decoded);
  if (!decodedUrl || !isAllowedRedirect(decodedUrl)) return null;

  const strippedUrl = parseUrl(stripped);
  if (strippedUrl && schemeOf(strippedUrl) === schemeOf(decodedUrl)) return stripped;
  return decoded;
};
